import { baseApi } from '@/lib/utils'

export interface TeamFolder {
  folder_id: number
  folder_name: string
}

export interface TeamListItem {
  id: number
  name: string
  folder_ids: number[]
  folders: TeamFolder[]
  leader_user_id: number | null
  leader_user_name: string | null
  leader_user_email: string | null
  leader_employee_id: number | null
  leader_employee_name: string | null
  leader_employee_email: string | null
  project_manager_id: number | null
  project_manager_name: string | null
  project_manager_email: string | null
  is_active: boolean
  user_member_count: number
  employee_member_count: number
  project_count: number
  created_at: string
  updated_at: string
}

export interface TeamUserMember {
  id: number
  name: string
  email: string
  status: string
}

export interface TeamEmployeeMember {
  user_id: number
  user_name: string
  user_email: string
  archived: boolean
}

export interface TeamProjectMember {
  project_id: number
  list_name: string
  FOLDER_ID: number
  folder_name: string
}

export interface TeamDetail {
  id: number
  name: string
  folder_ids: number[]
  folders: TeamFolder[]
  leader_user_id: number | null
  leader_user_name: string | null
  leader_user_email: string | null
  leader_employee_id: number | null
  leader_employee_name: string | null
  leader_employee_email: string | null
  project_manager_id: number | null
  project_manager_name: string | null
  project_manager_email: string | null
  is_active: boolean
  users: TeamUserMember[]
  employees: TeamEmployeeMember[]
  projects: TeamProjectMember[]
  created_at: string
  updated_at: string
}

export interface TeamsListResponse {
  success: boolean
  data: TeamListItem[]
}

export interface TeamDetailResponse {
  success: boolean
  data: TeamDetail
}

export interface CreateTeamPayload {
  name: string
  folder_ids?: number[]
  user_id?: number | null
  employee_id?: number | null
  project_manager_id?: number | null
  is_active?: boolean
}

export interface UpdateTeamPayload {
  id: number
  data: Partial<{
    name: string
    folder_ids: number[]
    user_id: number | null
    employee_id: number | null
    project_manager_id: number | null
    is_active: boolean
  }>
}

export interface TeamMembersPayload {
  id: number
  user_ids?: number[]
  employee_ids?: number[]
}

export interface ApiSuccess {
  success: boolean
  message?: string
  data?: unknown
}

export interface ClickupFoldersResponse {
  status: string
  data: TeamFolder[]
}

export const teamsApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getTeamsList: builder.query<TeamsListResponse, void>({
      query: () => ({ url: '/teams' }),
      providesTags: result =>
        result
          ? [
              ...result.data.map(t => ({ type: 'Teams' as const, id: t.id })),
              { type: 'Teams' as const, id: 'LIST' },
            ]
          : [{ type: 'Teams' as const, id: 'LIST' }],
    }),
    getTeamDetail: builder.query<TeamDetailResponse, number>({
      query: id => ({ url: `/teams/${id}` }),
      providesTags: (_r, _e, id) => [{ type: 'Teams' as const, id }],
    }),
    createTeam: builder.mutation<
      { success: boolean; message: string; data: { id: number } },
      CreateTeamPayload
    >({
      query: body => ({
        url: '/teams',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Teams', id: 'LIST' }, 'Employees', 'Projects'],
    }),
    updateTeam: builder.mutation<ApiSuccess, UpdateTeamPayload>({
      query: ({ id, data }) => ({
        url: `/teams/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Teams', id },
        { type: 'Teams', id: 'LIST' },
        'Employees',
        'Projects',
      ],
    }),
    deleteTeam: builder.mutation<ApiSuccess, number>({
      query: id => ({
        url: `/teams/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Teams', id: 'LIST' }, 'Employees', 'Projects'],
    }),
    assignTeamMembers: builder.mutation<
      {
        success: boolean
        data: { users_assigned: number; employees_assigned: number }
      },
      TeamMembersPayload
    >({
      query: ({ id, ...body }) => ({
        url: `/teams/${id}/members`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Teams', id },
        { type: 'Teams', id: 'LIST' },
        'Employees',
      ],
    }),
    getClickupFolders: builder.query<
      ClickupFoldersResponse,
      { pending?: boolean } | void
    >({
      query: args => ({
        url: '/employees/teams',
        ...(args &&
          args.pending !== undefined && {
            params: { pending: args.pending },
          }),
      }),
    }),
    removeTeamMembers: builder.mutation<ApiSuccess, TeamMembersPayload>({
      query: ({ id, ...body }) => ({
        url: `/teams/${id}/members`,
        method: 'DELETE',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Teams', id },
        { type: 'Teams', id: 'LIST' },
        'Employees',
      ],
    }),
  }),
})

export const {
  useGetTeamsListQuery,
  useGetTeamDetailQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
  useAssignTeamMembersMutation,
  useRemoveTeamMembersMutation,
  useGetClickupFoldersQuery,
} = teamsApi
